import * as React from "react";
import { Box, Grid, Card, Typography, ToggleButton, ToggleButtonGroup, Tooltip, IconButton } from "@mui/material";
import CardContent from '@mui/material/CardContent';
import Plot from "react-plotly.js";
import useWindowDimensions from "../../util/useWindowDimensions";
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import EditIcon from '@mui/icons-material/Edit';
import AddCommentIcon from '@mui/icons-material/AddComment';
import { groupBy, meanBy, minBy, maxBy, first } from 'lodash';
import { changeTimezone } from "../../util/dateFunctions";
import AnnotationDialog from "../../common/AnnotationDialog";

const BelcoUtilization = ({ accessToken, fullPlantReadings, tzName, plantAnnotations, appConfig, refreshPlantAnnotations, plantId, month, year }) => {
    const { windowHeight, windowWidth } = useWindowDimensions();
    const [annotationDialogOpen, setAnnotationDialogOpen] = React.useState(false);
    const [annotationToEdit, setAnnotationToEdit] = React.useState(null);

    const chartReadings = fullPlantReadings.map((reading) => {
        const dt = new Date(reading.TimestampAsLong);
        const convertedDt = changeTimezone(dt, tzName);
        return {
            RealPowerMw: reading.RealPowerMw,
            RealPowerDeliveredToGridMw: reading.RealPowerDeliveredToGridMw,
            Hour: convertedDt.getHours(),
            Day: convertedDt.getDay()
        };
    });

    const grpByDayAndHour = groupBy(chartReadings, (reading) => `${reading.Day}_${reading.Hour}`); 
    const aggPowerByHour = Object.keys(grpByDayAndHour).map((key) => {
        const arr = grpByDayAndHour[key];
        return {
            hour: first(arr).Hour,
            day: first(arr).Day,
            avg : meanBy(arr, (reading) => reading.RealPowerMw),
            min : minBy(arr, (reading) => reading.RealPowerMw),
            max : maxBy(arr, (reading) => reading.RealPowerMw)
        }
    });
    const aggGridByHour = Object.keys(grpByDayAndHour).map((key) => {
        const arr = grpByDayAndHour[key];
        return {
            hour: first(arr).Hour,
            day: first(arr).Day,
            avg : meanBy(arr, (reading) => reading.RealPowerDeliveredToGridMw),
            min : minBy(arr, (reading) => reading.RealPowerDeliveredToGridMw),
            max : maxBy(arr, (reading) => reading.RealPowerDeliveredToGridMw)
        }
    });

    const getObservationsAndRecommendations = () => {
        if (plantAnnotations == null) {
            return [];
        } else {
            let ret = plantAnnotations.filter(function (annotation) {
                return annotation.AnnotationLocation == "UTILOBS" && (annotation.AnnotationType == "OBS" || annotation.AnnotationType == "REC");
            });

            if (appConfig.canAccessInternalAnnotations == null || appConfig.canAccessInternalAnnotations == false) {
                ret = ret.filter(function (annotation) {
                    return annotation.InternalOnly == false;
                });
            }
            ret = ret.sort(function (a, b) {
                return a.SortOrder < b.SortOrder ? -1 : 1;
            })
            return ret;
        }
    };

    const handleNewComment = () => {
        setAnnotationToEdit(null);
        setAnnotationDialogOpen(true);
    };

    const handleEditComment = (annotation) => {
        setAnnotationToEdit(annotation);
        setAnnotationDialogOpen(true);
    };

    const handleCloseAnnotationDialog = () => {
        setAnnotationDialogOpen(false);
    }

    return (
        <Grid container spacing={0}>
        <Box>
        <Plot
            data={[
            {
                x: aggPowerByHour.map((agg) => agg.day).concat(aggPowerByHour.map((agg) => agg.day)).concat(aggPowerByHour.map((agg) => agg.day)),
                y: aggPowerByHour.map((agg) => agg.avg).concat(aggPowerByHour.map((agg) => agg.min['RealPowerMw'])).concat(aggPowerByHour.map((agg) => agg.max['RealPowerMw'])),
                type: 'scatter',
                mode: 'markers',
                marker: { color: '#2d9c89', size: 10 },
                xaxis: 'x',
                yaxis: 'y'
            },
            {
                x: aggGridByHour.map((agg) => agg.day),
                y: aggPowerByHour.map((agg) => agg.max['RealPowerDeliveredToGridMw']),
                type: 'box',
                whiskerwidth: 0.25,
                width: 0.25,
                opacity:  0.9,
                marker: { color: '#d2e8fa' },
                xaxis: 'x',
                yaxis: 'y3'
            },
            {
                x: aggPowerByHour.map((agg) => agg.hour).concat(aggPowerByHour.map((agg) => agg.hour)).concat(aggPowerByHour.map((agg) => agg.hour)),
                y: aggPowerByHour.map((agg) => agg.avg).concat(aggPowerByHour.map((agg) => agg.min['RealPowerMw'])).concat(aggPowerByHour.map((agg) => agg.max['RealPowerMw'])),
                type: 'scatter',
                mode: 'markers',
                marker: { color: '#2d9c89', size: 10 },
                xaxis: 'x2',
                yaxis: 'y2'
            },
            {
                x: aggGridByHour.map((agg) => agg.hour),
                y: aggPowerByHour.map((agg) => agg.max['RealPowerDeliveredToGridMw']),
                type: 'box',
                whiskerwidth: 0.25,
                width: 0.25,
                opacity:  0.9,
                marker: { color: '#d2e8fa' },
                xaxis: 'x2',
                yaxis: 'y4'
            },
            ]}
            layout={{
                showlegend: false,
                grid: {
                    rows: 2, 
                    columns: 1, 
                    pattern: 'independent',
                    ygap: 0.2
                },
                xaxis: {
                    title: {
                        text: 'Day of the Week'
                    },
                    tickmode: 'array',
                    tickvals: [0,1,2,3,4,5,6],
                    ticktext: ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'],
                    showticklabels: true
                },
                xaxis2: {
                    title: {
                        text: 'Hour of the Day'
                    },
                    tickmode: 'array',
                    tickvals: [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23],
                    ticktext: ['0','01','02','03','04','05','06','07','08','09','10','11','12','13','14','15','16','17','18','19','20','21','22','23'],
                    showticklabels: true,
                    // range: [-1,24]
                },
                yaxis: {
                    title: {
                        text: 'BESS MW',
                        font: {
                            size: 18,
                            color: 'teal'
                        }
                    },
                    tickfont: {
                        color: 'teal'
                    },
                    zerolinewidth: 1,
                    zerolinecolor: '#dbdbdb',
                    range: [-2,10],
                },
                yaxis3: {
                    title: {
                        text: 'Grid Load MW',
                        font: {
                            size: 18,
                            color: '#57b1fa'
                        }
                    },
                    tickfont: {
                        color: '#57b1fa'
                    },
                    range: [0,110],
                    anchor: 'x',
                    overlaying: 'y',
                    side: 'right'
                },
                yaxis2: {
                    title: {
                        text: 'BESS MW',
                        font: {
                            size: 18,
                            color: 'teal'
                        }
                    },
                    tickfont: {
                        color: 'teal'
                    },
                    zerolinewidth: 1,
                    zerolinecolor: '#dbdbdb',
                    range: [-2,10],
                },
                yaxis4: {
                    title: {
                        text: 'Grid Load MW',
                        font: {
                            size: 18,
                            color: '#57b1fa'
                        }
                    },
                    tickfont: {
                        color: '#57b1fa'
                    },
                    range: [0,110],
                    anchor: 'x2',
                    overlaying: 'y2',
                    side: 'right'
                },
                template: "simple_white",
                // boxmode: 'group',
                uirevision: 'true',
                title: {
                    text: 'BESS Utilization',
                    font: {
                        size: 20
                    }
                },
                width: windowWidth - 165,
                height: windowHeight - 300,
            }}
        />
        </Box>
        <Grid>
        <Card elevation={0}>
                <CardContent>
                    <Typography sx={{ fontSize: 26, fontWeight: 'bold', textDecoration: 'underline' }}>
                        Observations & Recommendations {appConfig.canAccessInternalAnnotations ? (<IconButton onClick={handleNewComment}><AddCommentIcon/></IconButton>) : null}
                    </Typography>
                            {
                                getObservationsAndRecommendations().map((annotation) => {
                                    return (
                                        <React.Fragment>
                                            <Typography sx={{ fontSize: 6 }}>&nbsp;</Typography>
                                            <Typography sx={{ fontSize: 16 }}>
                                                {annotation.InternalOnly ? <Tooltip title='Only visible to internal personnel'><AdminPanelSettingsIcon /></Tooltip> : null}
                                                {annotation.AnnotationType == "OBS" ? "Observation:" : "Recommendation:"}
                                                <Typography sx={{ fontSize: 16 }} display="inline"> {annotation.AnnotationText} </Typography>
                                                {appConfig.canAccessInternalAnnotations ? (<IconButton onClick={() => handleEditComment(annotation)}><EditIcon /></IconButton>) : null}
                                            </Typography>
                                        </React.Fragment>
                                    )
                                }
                                )
                            }
                        </CardContent>
                    </Card>
                <AnnotationDialog accessToken={accessToken} open={annotationDialogOpen} handleClose={handleCloseAnnotationDialog} annotationLocation={'UTILOBS'} selectableAnnotationTypes={['OBS', 'REC']} enableSortOrder={true} assetType={'plant'} assetId={plantId} month={month} year={year} appConfig={appConfig} toEdit={annotationToEdit} refreshPlantAnnotations={refreshPlantAnnotations} />
        </Grid>
    </Grid>
    );
};

export default BelcoUtilization;
